import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import DogPickerSheet from '../components/sales/DogPickerSheet';
import ItemPickerSheet from '../components/sales/ItemPickerSheet';
import PaymentSheet from '../components/sales/PaymentSheet';
import SaleLineItem from '../components/sales/SaleLineItem';
import TotalsCard from '../components/sales/TotalsCard';
import PaymentRow from '../components/sales/PaymentRow';
import BalanceCard from '../components/sales/BalanceCard';
import CashbackCard from '../components/sales/CashbackCard';
import { useSales } from '../hooks/useSales';
import { useCatalog } from '../hooks/useCatalog';
import { useAuth } from '../context/AuthContext';
import { PointsService } from '../services/pointsService';
import { computeSale, ILS } from '../utils/salesUtils';
import { calculateSizeTag } from '../utils/dogUtils';

import '../styles/pages/sales.css';

const API = process.env.REACT_APP_API_BASE_URL || '';

const AVATAR_COLORS = ['dog', 'dog2', 'dog3', 'dog4', 'dog5'];
const avatarColor = (str) => AVATAR_COLORS[(str?.charCodeAt?.(0) || 0) % AVATAR_COLORS.length];
const sizeOf = (dog) => {
  const tag = calculateSizeTag(dog?.weight).tag;
  return tag === '?' ? 'M' : tag;
};

export default function SaleBuilder() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { createSale, updateSale, addPayment, deletePayment, allSales, getSaleById } = useSales();
  const { catalog } = useCatalog();
  const { authorizedFetch } = useAuth();

  // Core sale state
  const [saleType, setSaleType] = useState('public'); // 'public' | 'dog'
  const [dogs, setDogs] = useState([]);
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState(null);
  const [payments, setPayments] = useState([]);
  const [internalNote, setInternalNote] = useState('');
  const [loadedSale, setLoadedSale] = useState(null);
  const [loadingSale, setLoadingSale] = useState(false);

  // UI state
  const [sheet, setSheet] = useState(null); // 'dog' | 'item' | 'pay'
  const [toast, setToast] = useState('');
  const [saving, setSaving] = useState(false);
  const [membershipWarning, setMembershipWarning] = useState('');
  const [bathPkgWarning, setBathPkgWarning] = useState('');

  // Cashback
  const [cashbackBalance, setCashbackBalance] = useState(0);
  const [cashbackApplied, setCashbackApplied] = useState(false);
  const [earnCashback, setEarnCashback] = useState(true);

  // Load existing sale when editing
  useEffect(() => {
    if (!id) return;
    let isMounted = true;
    setLoadingSale(true);

    getSaleById(id).then(data => {
      if (!isMounted) return;
      const existing = data || allSales.find(s => s._id === id || s.id === id);
      if (existing) {
        setLoadedSale(existing);
        const existingDogs = existing.dogs?.length > 0
          ? existing.dogs
          : (existing.dog ? [existing.dog] : []);
        if (existingDogs.length > 0) { setDogs(existingDogs); setSaleType('dog'); }
        else { setSaleType('public'); }
        if (existing.items) setItems(existing.items);
        if (existing.discount) setDiscount(existing.discount);
        if (existing.payments) setPayments(existing.payments);
        if (existing.internalNote) setInternalNote(existing.internalNote);
        setEarnCashback(existing.earnCashback !== false);
      }
      setLoadingSale(false);
    });

    return () => { isMounted = false; };
  }, [id, getSaleById]);

  // Fetch owner cashback balance whenever the primary dog changes
  useEffect(() => {
    const ownerId = dogs[0]?.owners?.[0]?._id;
    if (!ownerId || !authorizedFetch || id) { setCashbackBalance(0); setCashbackApplied(false); return; }
    PointsService.getBalance(authorizedFetch, ownerId)
      .then(data => setCashbackBalance(data.balance ?? 0))
      .catch(() => setCashbackBalance(0));
  }, [dogs, authorizedFetch, id]);

  // Compute how much cashback to apply: min(balance, what's left after other payments)
  const nonCashbackPaid = useMemo(
    () => payments.reduce((s, p) => s + (p.amount || 0), 0),
    [payments]
  );
  const { subtotal, discountAmount, total } = useMemo(
    () => computeSale(items, discount, []),
    [items, discount]
  );
  const nonCashbackRemaining = Math.max(0, total - nonCashbackPaid);
  const cashbackToApply = Math.min(cashbackBalance, nonCashbackRemaining);

  const effectivePayments = cashbackApplied && cashbackToApply > 0
    ? [...payments, { method: 'Cashback', amount: cashbackToApply }]
    : payments;

  const { paid, remaining, status } = useMemo(
    () => computeSale(items, discount, effectivePayments),
    [items, discount, effectivePayments]
  );

  const showToast = useCallback((msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }, []);

  // Items
  const handleQtyChange = useCallback((idx, delta) => {
    setItems(prev => prev.map((it, i) =>
      i === idx ? { ...it, qty: Math.max(1, (it.qty || 1) + delta) } : it
    ));
  }, []);

  const handleRemoveItem = useCallback((idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  }, []);

  // Payments (local only until save)
  const handleAddPayment = useCallback((p) => {
    setPayments(prev => [...prev, p]);
    setSheet(null);
  }, []);

  const handleRemovePayment = useCallback((idx) => {
    setPayments(prev => prev.filter((_, i) => i !== idx));
  }, []);

  // Discount
  const handleDiscountType = async (type) => {
    setMembershipWarning('');
    setBathPkgWarning('');

    if (saleType === 'dog' && dogs.length > 0) {
      const dogId = dogs[0]._id || dogs[0].id;
      const dogName = dogs[0].name || 'Este perro';

      if (type === 'membership') {
        try {
          const res = await authorizedFetch(`${API}/api/memberships?dogId=${dogId}&status=active`);
          if (res.ok) {
            const data = await res.json();
            const hasActive = (Array.isArray(data) ? data : []).some(m =>
              m.status === 'active' && (!m.endDate || new Date(m.endDate) >= new Date())
            );
            if (!hasActive) setMembershipWarning(`${dogName} no tiene una membresía activa. Verifica antes de guardar.`);
          }
        } catch (_) {}
      }

      if (type === 'bath-package') {
        try {
          const res = await authorizedFetch(`${API}/api/bath-packages?dogId=${dogId}&status=active`);
          if (res.ok) {
            const data = await res.json();
            const pkgs = Array.isArray(data) ? data : [];
            const hasActive = pkgs.some(p => p.status === 'active' && (p.baths?.length || 0) < (p.totalBaths || 5));
            if (!hasActive) setBathPkgWarning(`${dogName} no tiene un paquete de baños activo. Verifica antes de guardar.`);
          }
        } catch (_) {}
      }
    }

    setDiscount({ type, value: type === 'percent' ? 10 : type === 'amount' ? 50 : 0 });
  };
  const handleDiscountValue = (v) => setDiscount(d => ({ ...d, value: Math.max(0, parseFloat(v) || 0) }));
  const handleDiscountSwap = (type) => setDiscount(d => ({ ...d, type, value: type === 'percent' ? 10 : type === 'amount' ? 50 : 0 }));
  const handleRemoveDiscount = () => { setDiscount(null); setMembershipWarning(''); setBathPkgWarning(''); };

  // Save
  const handleSave = async () => {
    if (items.length === 0 || saving) return;
    setSaving(true);
    try {
      const dogIds = saleType === 'dog' ? dogs.map(d => d._id || d.id).filter(Boolean) : [];
      let saleId;

      if (id) {
        // Editing existing sale — patch items/discount/dogs/earnCashback/internalNote
        const patch = { items, discount, earnCashback, internalNote };
        if (dogIds.length > 0) { patch.dogs = dogIds; patch.dog = dogIds[0]; }
        await updateSale(id, patch);
        saleId = id;
      } else {
        // Creating new sale
        const sale = await createSale(dogIds, items, discount, { earnCashback, internalNote });
        saleId = sale?._id || sale?.id;
      }

      // Only add payments that are NEW (no _id yet — not previously saved)
      if (saleId) {
        const newPayments = effectivePayments.filter(p => !p._id);
        for (const p of newPayments) {
          await addPayment(saleId, p);
        }
      }

      const label =
        status === 'paid' ? 'Venta completada'
        : status === 'partial' ? `Venta guardada · ${ILS(remaining)} pendiente`
        : 'Venta guardada · sin pagar';
      showToast(label);
      navigate('/pos');
    } catch {
      showToast('Error al guardar la venta');
    } finally {
      setSaving(false);
    }
  };

  const ctaLabel = () => {
    if (items.length === 0) return 'Agrega ítems para comenzar';
    if (saleType === 'dog' && dogs.length === 0) return 'Selecciona un perro';
    if (discount?.type === 'membership') return 'Guardar · cubierto por membresía';
    if (discount?.type === 'bath-package') return 'Guardar · cubierto por paquete de baños';
    if (status === 'paid') return 'Completar venta';
    if (status === 'partial') return `Guardar · ${ILS(remaining)} pendiente`;
    if (status === 'due') return 'Guardar y cobrar';
    return 'Guardar borrador';
  };

  const primaryDog = dogs[0] || null;
  const dogColor = primaryDog ? avatarColor(primaryDog._id || primaryDog.id || primaryDog.name) : '';
  const ownerName = primaryDog?.owners?.[0]?.name || primaryDog?.ownerName || '';
  const ownerPhone = primaryDog?.owners?.[0]?.phone || primaryDog?.ownerPhone || '';
  const dogSizeResolved = primaryDog?._resolvedSize || (primaryDog ? sizeOf(primaryDog) : 'M');

  const activeSale = loadedSale || allSales.find(s => s._id === id || s.id === id);
  const saleTitle = id
    ? activeSale?.saleNumber
      ? `Venta #${String(activeSale.saleNumber).padStart(4, '0')}`
      : `Venta #${id.slice(-6).toUpperCase()}`
    : 'Nueva venta';

  if (id && loadingSale && items.length === 0) {
    return (
      <div className="pos-page pos-scope">
        <Navigation />
        <div className="pos-topbar">
          <button className="pos-back-btn" onClick={() => navigate('/pos')}>‹</button>
          <h1>Cargando venta…</h1>
          <div style={{ width: 38 }} />
        </div>
        <div className="pos-hint" style={{ marginTop: 40 }}>Cargando datos de la venta…</div>
      </div>
    );
  }

  return (
    <div className="pos-page pos-scope">
      <Navigation />

      {/* Top bar */}
      <div className="pos-topbar">
        <button className="pos-back-btn" onClick={() => navigate('/pos')}>‹</button>
        <h1>{saleTitle}</h1>
        <div style={{ width: 38 }} />
      </div>

      {/* Customer */}
      <div className="pos-section"><h2>Cliente</h2></div>
      <div className="pos-customer-card">
        {/* Sale type toggle */}
        <div className="pos-sale-type-toggle">
          <button
            className={`pos-sale-type-btn${saleType === 'public' ? ' active' : ''}`}
            onClick={() => { setSaleType('public'); setDogs([]); setMembershipWarning(''); setBathPkgWarning(''); }}
          >
            🏪 Venta pública
          </button>
          <button
            className={`pos-sale-type-btn${saleType === 'dog' ? ' active' : ''}`}
            onClick={() => setSaleType('dog')}
          >
            🐕 Con perro
          </button>
        </div>

        {saleType === 'public' ? (
          <div className="pos-customer-public">
            <span className="pos-customer-public-label">Sin perro asociado · cliente de mostrador</span>
          </div>
        ) : dogs.length === 0 ? (
          <div className="pos-customer-empty" onClick={() => setSheet('dog')}>
            <div className="pos-customer-avatar">🐾</div>
            <div className="pos-customer-empty-text">
              <strong>Seleccionar perro(s)</strong>
              <span>Podés seleccionar uno o varios perros</span>
            </div>
            <span className="pos-customer-chevron">›</span>
          </div>
        ) : (
          <div className="pos-customer-filled">
            {dogs.map((dog, idx) => {
              const dColor = avatarColor(dog._id || dog.id || dog.name);
              const dSize = dog._resolvedSize || sizeOf(dog);
              return (
                <div key={dog._id || dog.id || idx} className="pos-customer-row" style={{ marginBottom: idx < dogs.length - 1 ? 8 : 0 }}>
                  <div className={`pos-customer-avatar ${dColor}`}>🐕</div>
                  <div style={{ flex: 1 }}>
                    <div className="pos-customer-dog-name">{dog.name}</div>
                    <div className="pos-customer-dog-meta">
                      {dog.breed} · {dSize === 'S' ? 'Pequeño' : dSize === 'M' ? 'Mediano' : 'Grande'}
                      {dog.weight ? ` · ${dog.weight} kg` : ''}
                    </div>
                  </div>
                  <button
                    className="pos-customer-change-btn"
                    style={{ color: '#e53e3e' }}
                    onClick={() => setDogs(prev => prev.filter((_, i) => i !== idx))}
                  >
                    ✕
                  </button>
                </div>
              );
            })}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="pos-customer-change-btn" onClick={() => setSheet('dog')}>
                + Agregar perro
              </button>
            </div>
            {ownerName && (
              <div className="pos-customer-owner">
                <span style={{ fontSize: 14 }}>👤</span>
                <div className="pos-customer-owner-info">
                  <strong style={{ color: 'var(--pos-ink,#1F1A2E)', fontSize: 13 }}>{ownerName}</strong>
                  {ownerPhone && <span style={{ marginLeft: 4 }}>· {ownerPhone}</span>}
                </div>
                {ownerPhone && (
                  <button
                    className="pos-customer-call-btn"
                    onClick={() => window.open(`tel:${ownerPhone}`)}
                    aria-label="Llamar"
                  >
                    📞
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Items */}
      <div className="pos-section">
        <h2>Ítems · {items.length}</h2>
        {items.length > 0 && (
          <button className="pos-section-action" onClick={() => setSheet('item')}>+ Agregar</button>
        )}
      </div>
      <div className="pos-item-list">
        {items.map((it, i) => (
          <SaleLineItem
            key={i}
            item={it}
            index={i}
            onQtyChange={handleQtyChange}
            onRemove={handleRemoveItem}
          />
        ))}
        {items.length === 0 && (
          <div className="pos-hint">
            Sin ítems aún — agregá guardería, hotel, vacunas, veterinaria, estética o un cargo personalizado.
          </div>
        )}
        <button className="pos-add-item-btn" onClick={() => setSheet('item')}>
          <span style={{ fontSize: 18 }}>＋</span> Agregar ítem
        </button>
      </div>

      {/* Internal Note */}
      <div className="pos-section">
        <h2>Nota interna</h2>
      </div>
      <div className="pos-note-card">
        <div className="pos-note-header">
          <span className="pos-note-tag">🔒 Solo personal (no visible en recibo)</span>
          <span className="pos-note-counter">{internalNote.length}/200</span>
        </div>
        <textarea
          className="pos-note-input"
          rows={2}
          maxLength={200}
          placeholder="Escribí una nota interna sobre esta venta..."
          value={internalNote}
          onChange={e => setInternalNote(e.target.value.slice(0, 200))}
        />
      </div>

      {/* Discount */}
      {items.length > 0 && (
        <>
          <div className="pos-section">
            <h2>Descuento</h2>
            {discount && (
              <button className="pos-section-action" onClick={handleRemoveDiscount}>Quitar</button>
            )}
          </div>
          <div className="pos-discount-card">
            {!discount ? (
              <div className="pos-discount-picker">
                <button className="pos-discount-type-btn" onClick={() => handleDiscountType('percent')}>
                  % Porcentaje
                </button>
                <button className="pos-discount-type-btn" onClick={() => handleDiscountType('amount')}>
                  $ Monto
                </button>
                <button className="pos-discount-type-btn pos-discount-type-btn--mem" onClick={() => handleDiscountType('membership')}>
                  🏅 Membresía
                </button>
                <button className="pos-discount-type-btn pos-discount-type-btn--bath" onClick={() => handleDiscountType('bath-package')}>
                  🛁 Paquete baños
                </button>
              </div>
            ) : discount.type === 'membership' ? (
              <>
                <div className="pos-discount-membership">
                  <span className="pos-discount-mem-label">🏅 Membresía · cubierto</span>
                  <span className="pos-discount-computed">−{ILS(discountAmount)}</span>
                </div>
                {membershipWarning && (
                  <div className="pos-membership-warning">⚠️ {membershipWarning}</div>
                )}
              </>
            ) : discount.type === 'bath-package' ? (
              <>
                <div className="pos-discount-membership">
                  <span className="pos-discount-mem-label">🛁 Paquete de baños · cubierto</span>
                  <span className="pos-discount-computed">−{ILS(discountAmount)}</span>
                </div>
                {bathPkgWarning && (
                  <div className="pos-membership-warning">⚠️ {bathPkgWarning}</div>
                )}
              </>
            ) : (
              <div className="pos-discount-row">
                <div className="pos-discount-swap">
                  <button
                    className={`pos-discount-swap-btn${discount.type === 'percent' ? ' active' : ''}`}
                    onClick={() => handleDiscountSwap('percent')}
                  >%</button>
                  <button
                    className={`pos-discount-swap-btn${discount.type === 'amount' ? ' active' : ''}`}
                    onClick={() => handleDiscountSwap('amount')}
                  >$</button>
                </div>
                <input
                  className="pos-discount-input"
                  type="number"
                  value={discount.value}
                  onChange={e => handleDiscountValue(e.target.value)}
                />
                <span className="pos-discount-computed">−{ILS(discountAmount)}</span>
              </div>
            )}
          </div>
        </>
      )}

      {/* Totals */}
      {items.length > 0 && (
        <>
          <div className="pos-section"><h2>Total</h2></div>
          <TotalsCard
            subtotal={subtotal}
            discountAmount={discountAmount}
            discount={discount}
            total={total}
            earnCashback={earnCashback}
          />
          <label className="pos-earn-cashback-toggle">
            <input
              type="checkbox"
              checked={earnCashback}
              onChange={e => setEarnCashback(e.target.checked)}
            />
            <span>Otorgar cashback (1%) al propietario</span>
          </label>

          {/* Payments */}
          <div className="pos-section">
            <h2>Pagos · {payments.length}</h2>
            {remaining > 0 && total > 0 && (
              <button className="pos-section-action" onClick={() => setSheet('pay')}>+ Registrar</button>
            )}
          </div>
          <div className="pos-payments-wrap">
            {payments.map((p, i) => (
              <PaymentRow
                key={i}
                payment={p}
                onDelete={() => handleRemovePayment(i)}
              />
            ))}
            {cashbackApplied && cashbackToApply > 0 && (
              <PaymentRow
                payment={{ method: 'Cashback', amount: cashbackToApply }}
                onDelete={() => setCashbackApplied(false)}
              />
            )}
            {payments.length === 0 && !cashbackApplied && (
              <button className="pos-add-item-btn" onClick={() => setSheet('pay')}>
                <span style={{ fontSize: 18 }}>＋</span> Registrar primer pago
              </button>
            )}
            {!id && cashbackBalance > 0 && nonCashbackRemaining > 0 && (
              <CashbackCard
                balance={cashbackBalance}
                applyAmount={cashbackToApply}
                applied={cashbackApplied}
                onToggle={() => setCashbackApplied(v => !v)}
              />
            )}
            <BalanceCard
              remaining={remaining}
              total={total}
              onPayRest={remaining > 0 && effectivePayments.length > 0 ? () => setSheet('pay') : null}
            />
          </div>
        </>
      )}

      {/* Sticky CTA */}
      <div className="pos-sticky-cta">
        <button
          className="pos-cta-btn"
          disabled={items.length === 0 || (saleType === 'dog' && dogs.length === 0) || saving}
          onClick={handleSave}
        >
          {saving ? 'Guardando…' : ctaLabel()}
        </button>
      </div>

      {/* Sheets */}
      {sheet === 'dog' && (
        <DogPickerSheet
          onClose={() => setSheet(null)}
          selectedDogs={dogs}
          onPick={(picked) => {
            // Merge: keep existing dogs not in picker results, add newly picked
            setDogs(picked);
            setSheet(null);
          }}
        />
      )}
      {sheet === 'item' && (
        <ItemPickerSheet
          dogSize={dogSizeResolved}
          catalog={catalog}
          onClose={() => setSheet(null)}
          onAdd={(it) => { setItems(prev => [...prev, it]); setSheet(null); }}
        />
      )}
      {sheet === 'pay' && (
        <PaymentSheet
          remaining={remaining}
          onClose={() => setSheet(null)}
          onSave={handleAddPayment}
        />
      )}

      {toast && <div className="pos-toast">{toast}</div>}
    </div>
  );
}
